import { useState, useEffect } from "react";
import {
    Box,
    Stack,
    Button,
    Tooltip,
    CircularProgress
} from '@mui/material';

import { styled } from '@mui/system';

import { sendQuoteForConversion } from '../services/api';
import DetailTable from './DetailTable';
import ConversionModal from './ConversionModal';
import { QUOTE_STATUS } from '../helpers/constants';

const QuoteTableFooter = ({ option }) => {
    return (        
        <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={1}
            marginTop={1}
        >
            <Box>
                <b>SALES TAX: </b>
                ${option.sales_tax_amount}
            </Box>
            <Box>
                <b>QUOTATION TOTAL: </b>
                ${option.total_amount}
            </Box>
        </Stack>
    )
}


export default function QuoteTable({ option, quote, onStatusUpdate, features }) {
    const [isLoading, setIsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [conversionData, setConversionData] = useState(null);
    const [error, setError] = useState(null);

    const { enableConvertWorkOrder = false} = features || {};

    useEffect(() => {
        const closeModal = (e) => {
            if (e.keyCode === 27) {
              handleModalClose();
            }
        }
        window.addEventListener('keydown', closeModal);
        return () => window.removeEventListener('keydown', closeModal);
    }, [])

    const handleModalClose = () => {
        setConversionData(null);
        setModalOpen(false);
        setError(null);
    }
    

    const ConvertButton = styled(Button)({
        border: '1px solid',
        borderRadius: 0,
        color: '#FFFFFF',
        backgroundColor: '#BE161D',
        '&:hover': {
            backgroundColor: '#D6D9DD',
            color: '#000000',
            borderColor: '#ADADAD'
        }
    });

    const canConvertQuote = (status) => {
        //ensure only open quotes and SFCC source quotes can be converted
        return status === QUOTE_STATUS.OPEN;
    }

    const isProcessing = quote.status_code === QUOTE_STATUS.PROCESSING;

    const convertQuote = async (quote, quoteOption) => {
        try {
            setIsLoading(true);
            await sendQuoteForConversion(quote, quoteOption); //non-201 response will throw error
            //disable button by setting status_code locally for all options
            onStatusUpdate(QUOTE_STATUS.PROCESSING, quote.quote_id); //4 = Processing
            setConversionData({ quote:quote, option:quoteOption});
            setModalOpen(true);
        } catch(err){
            setError({
                message:err.message,
                quote: quote,
                option: quoteOption
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box sx={{ margin: 1 }}>
            <b>{`OPTION ${option.option_no}`}</b>
            {quote.source_system_code === 'SFCC' && enableConvertWorkOrder && (
                <>
                {isLoading? <CircularProgress/> : 
                    <Tooltip title={!canConvertQuote(quote.status_code) ? "Work Order cannot be created" : null}>
                        <span>
                            <ConvertButton
                                disabled={!canConvertQuote(quote.status_code)}
                                sx={{
                                    height: '32px',
                                    minHeight: '32px',
                                    marginLeft: '60px',
                                    marginBottom: '5px',
                                    borderColor: '#1A2226',
                                    '&.Mui-disabled': {
                                        backgroundColor: '#968984',
                                        color: '#FFFFFF',
                                        borderColor: '#726c6a'
                                    }
                                }}
                                variant={!canConvertQuote(quote.status_code)? 'outlined' : 'contained'}
                                onClick={() => convertQuote(quote, option.option_no)}
                            >
                                {isProcessing ? (
                                    <>
                                        <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                                            Processing...
                                    </>
                                ): ('Create a work order')}
                            </ConvertButton>
                        </span>
                    </Tooltip>
                } </>
            )}
            <DetailTable data={option.details} />
            <QuoteTableFooter option={option} />
            <ConversionModal 
                data={conversionData}
                open={!!modalOpen || !!error}
                handleClose={handleModalClose}
                error={error}
            />
        </Box>    
    )
}
